import { Component, OnInit, OnDestroy, Input, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { Location, DatePipe } from '@angular/common';
import { Subscription, combineLatest, from } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { HttpHelpService } from '../../Data/service/http-help.service';
import { AVAILABLEDOCUMENTTYPES, AVAILABLEDOCUMENTLANGUAGES } from './document.data';
import { DocumentResponse, SoldItem, Company, Customer } from './document.model';
import { PaymentTotals, PaymentMethod } from './document-payments/document-payments.model';
import { DocumentPaymentsComponent } from './document-payments/document-payments.component';
import { CashPaymentLine } from '../models/cash-payment.model';
import { CreditCardPaymentLine } from '../models/credit-card-payment.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AllocationDenialService } from '../../services/allocation-denial.service';

/**
 * Component responsible for creating and editing various document types (invoices, receipts, etc.).
 * Handles form management, data loading/saving, validation, and interaction with child components.
 */
@Component({
	selector: 'app-document',
	templateUrl: './document.component.html',
	styleUrls: ['./document.component.scss'],
	providers: [DatePipe, MessageService]
})
export class DocumentComponent implements OnInit, OnDestroy {
	noPaymentMethods: boolean = false;
	/** The type identifier of the document being handled (e.g., '320', '400'). */
	documentType: string | null = null;
	/** The unique identifier (UUID) of the document when in edit mode. */
	documentId: string | null = null;
	/** The display ID (numeric or string) of the document, often different from the UUID. */
	displayDocumentId: number | string | null = null;
	/** The current mode of the component: 'create' or 'edit'. */
	mode: 'create' | 'edit' = 'create';
	/** Flag to enable/disable detailed console logging for debugging. */
	debug: boolean = false;
	/** Subscription to route parameters and query parameters. */
	private routeSub: Subscription;
	/** The main reactive form group encapsulating all document data. */
	documentForm: FormGroup;
	/** The currently selected language code (e.g., 'en', 'he'). */
	curentLang: string = localStorage.getItem('curentLang') || 'en';
	/** Flag indicating if data is currently being loaded or saved. */
	isLoading: boolean = false;
	/** Flag indicating if the selected month for the document date is closed (read-only). */
	isMonthClosed: boolean = false;
	/** Flag indicating if the form has been submitted (used for validation display). */
	formSubmitted: boolean = false;
	/** Flag indicating if a save/confirm operation is in progress. */
	isSubmitting: boolean = false;
	/** The current VAT percentage applicable based on type and date. */
	currentVatPercentage: number = 0;
	/** List of available document languages. */
	availableLanguages = AVAILABLEDOCUMENTLANGUAGES;
	/** List of available document types. */
	availableDocumentTypes = AVAILABLEDOCUMENTTYPES;
	/** Stores the prepared payload for debugging purposes before sending to the API. */
	preparedPayloadForDebug: any = null;
	/** Array of selected payment method identifiers. */
	selectedPaymentMethod: string[] = [];
	/** Array of companies for validation purposes. */
	companies: any[] = [];
	/** Flag indicating if the company select should be disabled (when inside a document page). */
	disableCompanySelect: boolean = false;

	/** Flag controlling the visibility of the document lines section based on document type. */
	showLinesSection: boolean = false;
	/** Flag controlling the visibility of the payments section based on document type. */
	showPaymentsSection: boolean = false;
	/** Flag indicating if VAT is applicable for the current document type. */
	isVatApplicable: boolean = false;

	/** Reference to the child DocumentPaymentsComponent. */
	@ViewChild(DocumentPaymentsComponent) paymentsComponent: DocumentPaymentsComponent;
	@ViewChild('missingCustomerInfoModal') missingCustomerInfoModal: any;
    private yearlyMaxAmount: number | null = null;
    private lastCheckedCustomer: any = null;

	// Add these state variables to the class:
	dialogVisible: boolean = false;
	dialogType: 'vat' | 'id' | null = null;
	dialogStep: 'warning' | 'edit' = 'warning';
	dialogInputValue: string = '';
	dialogAction: 'save' | 'confirm' = 'save';
	dialogCustomerId: any = null;
	dialogResolve: ((result: any) => void) | null = null;
	taxAutorityDialogVisible: boolean;
	taxAutorityMissing: boolean;

	/**
	 * Initializes the component with dependency injection.
	 * @param route Provides access to route parameters and query parameters.
	 * @param router Service for programmatic navigation.
	 * @param fb Service for building reactive forms.
	 * @param el Reference to the host element of the component.
	 * @param datePipe Service for formatting dates.
	 * @param location Service for interacting with the browser's history.
	 * @param spinner Service for displaying loading spinners.
	 * @param service Service for making HTTP requests related to documents.
	 * @param translate Service for handling internationalization.
	 * @param messageService Service for displaying messages (toasts).
	 * @param cdr Service for manually triggering change detection.
	 */
	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private fb: FormBuilder,
		private el: ElementRef,
		private datePipe: DatePipe,
		private location: Location,
		private spinner: NgxSpinnerService,
		private service: HttpHelpService,
		private translate: TranslateService,
		private messageService: MessageService,
		private cdr: ChangeDetectorRef,
		private modalService: NgbModal,
		private allocationDenialService: AllocationDenialService
	) { }

	/**
	 * Updates the selected payment methods based on the child component's output.
	 * @param methods An array of selected payment method identifiers.
	 */
	onSelectedPaymentMethodChange(methods: string[]) {
		this.selectedPaymentMethod = methods;
		this.paymentsFormGroup.get('selectedPaymentMethod')?.setValue(methods);
	}

	/**
	 * Check if we're in a document page and disable company select accordingly
	 */
	private checkIfInDocumentPage(): void {
		const currentUrl = this.router.url;
		// Since we're already in a document component, we should disable the company select
		// This prevents changing company while creating/editing a document
		this.disableCompanySelect = true;
	}

	/**
	 * Angular lifecycle hook called upon component initialization.
	 * Initializes the main form and subscribes to route changes.
	 */
	ngOnInit(): void {
		this.initializeMainForm();
		this.loadCompanies();
		
		// Check if we're in a document page and disable company select
		this.checkIfInDocumentPage();

		this.routeSub = combineLatest([
			this.route.params,
			this.route.queryParams
		]).subscribe(([params, queryParams]: [Params, Params]) => {
			const docTypeFromRoute = this.route.snapshot.data['docType'];
			let qp = { ...queryParams };
			if (docTypeFromRoute) {
				qp['type'] = docTypeFromRoute;
			}
			this.processRouteData(params, qp);
		});

		const paymentsFormGroup = this.paymentsFormGroup;
		const paymentArrays = [
			paymentsFormGroup.get('linesCash'),
			paymentsFormGroup.get('linesChecks'),
			paymentsFormGroup.get('linesBankPayment'),
			paymentsFormGroup.get('linesCredit')
		];
		paymentArrays.forEach(arr => {
			if (arr) {
				arr.valueChanges.subscribe(() => {
					if (arr.touched && this.noPaymentMethods) {
						this.noPaymentMethods = false;
						this.cdr.detectChanges();
					}
				});
			}
		});

		this.linesFormArray.valueChanges.subscribe(() => {
			this.recalculateAllLinesAndSummary();
		});
	}

	/**
	 * Processes route parameters and query parameters to determine component state.
	 * Sets mode, document ID, document type, and loads data if necessary.
	 * @param params Route parameters.
	 * @param queryParams Query parameters.
	 * @private
	 */
	private processRouteData(params: Params, queryParams: Params): void {
this.updatedebug(queryParams);
		this.determineModeAndId(params);
		const requestedType = queryParams['type'] || null;
		let newDocumentType: string | null = null;
		let needsRedirect = false;
		if (this.mode === 'edit' && this.documentId) {
			this.loadDocumentData(this.documentId);
		} else {
			const result = this.handleCreateModeType(requestedType);
			newDocumentType = result.type;
			needsRedirect = result.redirect;
			if (needsRedirect) {
				this.redirectToDefaultType();
				return;
			}
			this.updateDocumentType(newDocumentType);
			if (this.documentType && this.headFormGroup.get('invoice_type')?.value !== this.documentType) {
				this.headFormGroup.patchValue({ invoice_type: this.documentType });
			}
		}
this.triggerInitialChecks();
	}

	/**
	 * Loads existing document data from the backend when in 'edit' mode.
	 * Populates the form with the fetched data.
	 * @param documentUuid The UUID of the document to load.
	 */
	loadDocumentData(documentUuid: string): void {
		if (this.isLoading) return;
		this.isLoading = true;
		this.spinner.show();
		this.service.GetInvoiceForUpdate(documentUuid).subscribe({
			next: (res: DocumentResponse) => {
				if (!res) {
					this.messageService.add({
						severity: 'error',
						summary: this.translate.instant('notifications.errorSummary'),
						detail: this.translate.instant('notifications.loadEntityErrorDetail', { entity: this.translate.instant('routes.document.back') })
					});
					this.isLoading = false;
					this.spinner.hide();
					return;
				}

				const fetchedDocType = String(res.invoice_type);
				this.displayDocumentId = res.invoice_id;

				this.updateDocumentType(fetchedDocType);

				const headForm = this.documentForm.get('head');
				if (headForm) {
					headForm.patchValue({
						company_id: res.company_id,
						customer_id: res.customer_id,
						invoice_date: res.invoice_date,
						vat_list_id: res.vat_list_id,
						vat_percentage: res.vat_percentage,
						document_language: res.document_language,
						currency: res.currency,
						invoice_type: fetchedDocType
					}, { emitEvent: false });
					} else {
					console.error("[DEBUG/DocumentComponent] Head form group not found during patching!");
				}

				this.documentForm.patchValue({
					invoice_note: res.invoice_note,
					allocation_number: String(res.allocation_number ?? '0'),
					send_to_customer: res.send_to_customer || false,
					customer_name: res.customer_name,
					customer_id_number: res.customer_id_number,
					customer_vat_number: res.customer_vat_number,
					amount_before_discount: parseFloat(res.amount_before_discount) || 0,
					all_discount: parseFloat(res.all_discount) || 0,
					payment_amount: parseFloat(res.payment_amount) || 0,
					all_vat_amount: parseFloat(res.all_vat_amount) || 0,
					payment_amount_including_vat: parseFloat(res.payment_amount_including_vat) || 0,
				}, { emitEvent: false });

				if (this.showLinesSection) {
					this.populateFormArray(res.soldItems || [], 'lines');
					this.recalculateAllLinesAndSummary();
				} else {
					this.linesFormArray.clear();
					this.documentForm.patchValue({
						amount_before_discount: parseFloat(res.amount_before_discount) || 0,
						all_discount: parseFloat(res.all_discount) || 0,
						payment_amount: parseFloat(res.payment_amount) || 0,
						all_vat_amount: parseFloat(res.all_vat_amount) || 0,
						payment_amount_including_vat: parseFloat(res.payment_amount_including_vat) || 0,
					}, { emitEvent: false });
					this.recalculateAllLinesAndSummary();
				}

				if (this.showPaymentsSection && this.documentForm.contains('payments')) {
					const receipts = Array.isArray(res.receiptsDetails) ? res.receiptsDetails : [];
					(this.paymentsFormGroup.get('linesCash') as FormArray)?.clear();
					(this.paymentsFormGroup.get('linesChecks') as FormArray)?.clear();
					(this.paymentsFormGroup.get('linesBankPayment') as FormArray)?.clear();
					(this.paymentsFormGroup.get('linesCredit') as FormArray)?.clear();

					receipts.forEach(line => {
						const lineDate = line.date ? new Date(line.date) : new Date();
						lineDate.setHours(0, 0, 0, 0);
						const group = this.fb.group({
							receipt_detail_id: line.id,
							payment_type: line.payment_type,
							date: [lineDate, Validators.required],
							amount: parseFloat(line.amount) || 0,
							bank: line.bank,
							branch: line.branch,
							account: line.account,
							check: line.check,
							credit_card_company_code: line.credit_card_code,
							card_type_code: line.credit_type,
							last_4_digits: null,
							payment_number: line.line_id,
							details: line.details,
							reference: line.reference,
							credit_card_name: line.credit_card_name,
						});
						switch (line.payment_type) {
							case 1: // Cash
								(this.paymentsFormGroup.get('linesCash') as FormArray).push(group);
								break;
							case 2: // Check
								(this.paymentsFormGroup.get('linesChecks') as FormArray).push(group);
								break;
							case 3: // Credit
								(this.paymentsFormGroup.get('linesCredit') as FormArray).push(group);
								break;
							case 4: // Bank Payment
								(this.paymentsFormGroup.get('linesBankPayment') as FormArray).push(group);
								break;
							default:
								break;
						}
					});
				} else if (this.documentForm.contains('payments')) {
					(this.paymentsFormGroup.get('linesCash') as FormArray)?.clear();
					(this.paymentsFormGroup.get('linesChecks') as FormArray)?.clear();
					(this.paymentsFormGroup.get('linesBankPayment') as FormArray)?.clear();
					(this.paymentsFormGroup.get('linesCredit') as FormArray)?.clear();
				}

				this.isLoading = false;
				this.spinner.hide();
				this.cdr.detectChanges();
			},
			error: (error) => {
				console.error("[DEBUG/DocumentComponent] Error loading document data:", error);
				this.messageService.add({
					severity: 'error',
					summary: this.translate.instant('notifications.errorSummary'),
					detail: this.translate.instant('notifications.loadEntityErrorDetail', { entity: this.translate.instant('routes.document.back') })
				});
				this.isLoading = false;
				this.displayDocumentId = null;
				this.spinner.hide();
			}
		});
	}

	/**
	 * Populates a specific FormArray within the main form with data.
	 * @param items The array of data items (e.g., SoldItem) to populate the FormArray with.
	 * @param formArrayName The name of the FormArray control within `documentForm`.
	 * @private
	 */
	private populateFormArray(items: SoldItem[], formArrayName: string): void {
		const formArray = this.documentForm.get(formArrayName) as FormArray;
		formArray.clear();
items.forEach((item, index) => {
			const lineGroup = this.fb.group({
				index: index + 1,
				product_id: item.product_id,
				category: item.category,
				measure_unit_description: item.measure_unit_description,
				measurement_unit_id: item.measurement_unit_id,
				description: item.description,
				catalog_id: item.catalog_id,
				item_name: item.item_name,
				quantity: parseFloat(item.quantity as any) || 0,
				price_per_unit: parseFloat(item.price_per_unit as any) || 0,
				discount: parseFloat(item.discount as any) || 0,
				sub_total: { value: parseFloat(item.sub_total as any) || 0, disabled: true },
				vat_rate: { value: parseFloat(item.vat_rate as any) || 0, disabled: false },
				vat_amount: { value: parseFloat(item.vat_amount as any) || 0, disabled: true },
				total_amount: { value: parseFloat(item.total_amount as any) || 0, disabled: false },
				isVatIncluded: item.isVatIncluded || false,
				sold_item_id: item.sold_item_id || null
			});
			formArray.push(lineGroup);
		});
	}

	/**
	 * Updates the debug flag based on query parameters.
	 * @param queryParams Query parameters from the route.
	 * @private
	 */
	private updatedebug(queryParams: Params): void {
		const initialDebugCheck = !this.debug;
		if (queryParams['debug'] === 'true') {
			this.debug = true;
		}
	}

	/**
	 * Determines the component mode ('create' or 'edit') and sets the document ID based on route parameters.
	 * @param params Route parameters.
	 * @private
	 */
	private determineModeAndId(params: Params): void {
		this.documentId = params['id'] || null;
		if (this.documentId) {
			this.mode = 'edit';
		} else {
			this.mode = 'create';
			this.documentId = null;
}
	}

	/**
	 * Determines the document type to use in 'create' mode based on the requested type from query parameters.
	 * Handles redirection if the requested type is invalid or missing.
	 * @param requestedType The document type requested via the 'type' query parameter.
	 * @returns An object indicating the determined type and whether a redirect is needed.
	 * @private
	 */
	private handleCreateModeType(requestedType: string | null): { type: string | null, redirect: boolean } {
		if (requestedType && this.availableDocumentTypes.some(t => t.id === requestedType)) {
return { type: requestedType, redirect: false };
		} else {
			const defaultType = '320';
if (requestedType !== defaultType) {
				return { type: null, redirect: true };
			} else {
return { type: defaultType, redirect: false };
			}
		}
	}

	/**
	 * Redirects the user to the default document type ('320') URL.
	 * @private
	 */
	private redirectToDefaultType(): void {
		const defaultType = '320';
this.router.navigate(['../document'], {
			relativeTo: this.route,
			queryParams: { type: defaultType },
			queryParamsHandling: 'merge',
			replaceUrl: true
		});
	}

	/**
	 * Updates the component's document type state and triggers necessary form updates and data fetches.
	 * @param newDocumentType The new document type identifier.
	 * @private
	 */
	private updateDocumentType(newDocumentType: string | null): void {
		if (this.documentType !== newDocumentType) {
			const previousType = this.documentType;
			this.documentType = newDocumentType;
if (this.documentForm && this.headFormGroup.get('invoice_type')?.value !== this.documentType) {
				this.headFormGroup.patchValue({ invoice_type: this.documentType }, { emitEvent: false });
			} else if (this.documentForm) {
				}

			this.updateFormStateForDocumentType(this.documentType);
			// Expose documentForm globally for interceptor fallback
			(window as any).documentForm = this.documentForm;
			if (this.isVatApplicable) {
				this.getVatPercentageByVatListIdAndDate();
			} else {
				this.headFormGroup.patchValue({ vat_percentage: 0 });
			}

			if (this.showLinesSection) {
				this.recalculateAllLinesAndSummary();
			} else {
				}
			this.cdr.detectChanges();
		} else {
			}
	}

	/**
	 * Updates the form's structure, visibility flags, and validators based on the selected document type.
	 * Enables/disables sections like lines, payments, and VAT controls.
	 * @param docType The current document type identifier.
	 * @private
	 */
	private updateFormStateForDocumentType(docType: string | null): void {
		if (!this.documentForm) return;

		this.showLinesSection = true;
		this.showPaymentsSection = false;
		this.isVatApplicable = true;
		const linesArray = this.linesFormArray;
		const paymentsGroup = this.paymentsFormGroup;
		const vatListIdControl = this.headFormGroup.get('vat_list_id');
		const allocationNumberControl = this.documentForm.get('allocation_number');

		linesArray.disable({ emitEvent: false });
		paymentsGroup?.disable({ emitEvent: false });
		vatListIdControl?.disable({ emitEvent: false });

		linesArray.clearValidators();
		paymentsGroup?.clearValidators();

		switch (docType) {
			case '305':
				this.showLinesSection = true;
				this.showPaymentsSection = false;
				this.isVatApplicable = true;
				linesArray.enable({ emitEvent: false });
				linesArray.setValidators(Validators.required);
				vatListIdControl?.enable({ emitEvent: false });
				linesArray.setValidators([Validators.required, Validators.minLength(1)]);
				allocationNumberControl?.setValue('1', { emitEvent: true });
				allocationNumberControl?.enable({ emitEvent: false });
				break;
			case '332':
				this.showLinesSection = true;
				this.showPaymentsSection = false;
				this.isVatApplicable = true;
				linesArray.enable({ emitEvent: false });
				linesArray.setValidators(Validators.required);
				vatListIdControl?.enable({ emitEvent: false });
				linesArray.setValidators([Validators.required, Validators.minLength(1)]);
				allocationNumberControl?.setValue('0', { emitEvent: true });
				allocationNumberControl?.enable({ emitEvent: false });
				break;
			case '100':
				this.showLinesSection = true;
				this.showPaymentsSection = false;
				this.isVatApplicable = true;
				linesArray.enable({ emitEvent: false });
				linesArray.setValidators(Validators.required);
				vatListIdControl?.enable({ emitEvent: false });
				linesArray.setValidators([Validators.required, Validators.minLength(1)]);
				allocationNumberControl?.enable({ emitEvent: false });
				break;
			case '0':
				this.showLinesSection = true;
				this.showPaymentsSection = false;
				this.isVatApplicable = true;
				linesArray.enable({ emitEvent: false });
				linesArray.setValidators(Validators.required);
				vatListIdControl?.enable({ emitEvent: false });
				linesArray.setValidators([Validators.required, Validators.minLength(1)]);
				allocationNumberControl?.setValue('0', { emitEvent: true });
				allocationNumberControl?.disable({ emitEvent: false });
				this.cdr.detectChanges();
				break;
			case '320':
				this.showLinesSection = true;
				this.showPaymentsSection = true;
				this.isVatApplicable = true;
				linesArray.enable({ emitEvent: false });
				linesArray.setValidators(Validators.required);
				paymentsGroup?.enable({ emitEvent: false });
				vatListIdControl?.enable({ emitEvent: false });
				linesArray.setValidators([Validators.required, Validators.minLength(1)]);
				allocationNumberControl?.setValue('1', { emitEvent: true });
				allocationNumberControl?.enable({ emitEvent: false });
				allocationNumberControl?.markAsDirty();
				allocationNumberControl?.markAsTouched();
				this.cdr.detectChanges();
				break;
			case '330':
			case '200':
				this.showLinesSection = true;
				this.showPaymentsSection = false;
				this.isVatApplicable = true;
				linesArray.enable({ emitEvent: false });
				linesArray.setValidators(Validators.required);
				vatListIdControl?.enable({ emitEvent: false });
				linesArray.setValidators([Validators.required, Validators.minLength(1)]);
				allocationNumberControl?.setValue('0', { emitEvent: true });
				allocationNumberControl?.enable({ emitEvent: false });
				allocationNumberControl?.markAsDirty();
				allocationNumberControl?.markAsTouched();
				this.cdr.detectChanges();
				break;
			case '400':
				this.showLinesSection = false;
				this.showPaymentsSection = true;
				this.isVatApplicable = false;
				linesArray.disable({ emitEvent: false });
				linesArray.clearValidators();
				paymentsGroup?.enable({ emitEvent: false });
				vatListIdControl?.disable({ emitEvent: false });
				allocationNumberControl?.setValue('0', { emitEvent: true });
				allocationNumberControl?.enable({ emitEvent: false });
				allocationNumberControl?.markAsDirty();
				allocationNumberControl?.markAsTouched();
				this.cdr.detectChanges();
				break;
			default:
				console.warn(`[DEBUG/DocumentComponent] Unhandled document type: ${docType}. Applying default state (likely incorrect).`);
				this.showLinesSection = false;
				this.showPaymentsSection = false;
				this.isVatApplicable = false;
				linesArray.disable({ emitEvent: false });
				vatListIdControl?.disable({ emitEvent: false });
				this.headFormGroup.patchValue({ vat_percentage: 0 }, { emitEvent: false });
				allocationNumberControl?.setValue('0', { emitEvent: true });
				allocationNumberControl?.enable({ emitEvent: false });
				allocationNumberControl?.markAsDirty();
				allocationNumberControl?.markAsTouched();
				this.cdr.detectChanges();
				break;
		}

		linesArray.updateValueAndValidity({ emitEvent: false });
		paymentsGroup?.updateValueAndValidity({ emitEvent: false });
		vatListIdControl?.updateValueAndValidity({ emitEvent: false });
	}

	/**
	 * Angular lifecycle hook called just before the component is destroyed.
	 * Unsubscribes from active subscriptions.
	 */
	ngOnDestroy(): void {
		if (this.routeSub) {
			this.routeSub.unsubscribe();
		}
	}

	/**
	 * Handles the selection of a new document type from the UI in 'create' mode.
	 * Navigates to update the URL query parameter, triggering a state update.
	 * Does nothing in 'edit' mode.
	 * @param newType The identifier of the newly selected document type.
	 */
	changeDocumentType(newType: string): void {
if (this.mode === 'create' && this.documentType !== newType) {
this.router.navigate([], {
				relativeTo: this.route,
				queryParams: { type: newType },
				queryParamsHandling: 'merge',
				replaceUrl: true
			});
		} else if (this.mode === 'edit') {
			console.warn('Cannot change document type while in edit mode.');
} else {
}
	}

	/**
	 * Gets the display name of the current document type.
	 * @returns The name of the current document type, or 'Unknown Document' if not found.
	 */
	getCurrentDocumentTypeName(): string {
		const current = this.availableDocumentTypes.find(t => t.id === this.documentType);
		if (current) {
			switch (this.curentLang) {
				case 'en':
					return current.name_en || 'Unknown Document';
				case 'ar':
					return current.name_ar || current.name_en || 'Unknown Document';
				case 'he':
					return current.name_he || current.name_en || 'Unknown Document';
				default:
					return current.name_en || 'Unknown Document';
			}
		}
		return 'Unknown Document';
	}

	/**
	 * Initializes the main reactive form (`documentForm`) with its structure, default values, and validators.
	 */
	initializeMainForm(): void {
		const defaultCompanyId = JSON.parse(localStorage.getItem('companyDefault') || 'null');
		const defaultDate = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
		const defaultVatType = 8;
		const defaultCurrency = 'ILS';
		const userId = JSON.parse(localStorage.getItem('user_id') || 'null');
		const merchantId = JSON.parse(localStorage.getItem('merchant_id') || 'null');
		this.documentForm = this.fb.group({
		  merchant_id: [merchantId],
		  created_by: [userId],
		  head: this.fb.group({
			invoice_type: [this.documentType],
			company_id: [defaultCompanyId, Validators.required],
			customer_id: [null, Validators.required],
			invoice_date: [defaultDate, Validators.required],
			vat_list_id: [defaultVatType, Validators.required],
			vat_percentage: [{ value: 0, disabled: true }],
			document_language: [this.curentLang, Validators.required],
			currency: [defaultCurrency, Validators.required],
		  }),
		  lines: this.fb.array([]),
		  payments: this.fb.group({
			linesCash: this.fb.array([]),
			linesChecks: this.fb.array([]),
			linesBankPayment: this.fb.array([]),
			linesCredit: this.fb.array([]),
			cash_amount: [{ value: 0, disabled: true }],
			checks_amount: [{ value: 0, disabled: true }],
			bank_payment_amount: [{ value: 0, disabled: true }],
			credit_amount: [{ value: 0, disabled: true }],
			deduction_amount: [{ value: 0, disabled: true }],
			selectedPaymentMethod: [[]]
		  }),
		  amount_before_discount: [{ value: 0, disabled: true }],
		  all_discount: [{ value: 0, disabled: true }],
		  payment_amount: [{ value: 0, disabled: true }],
		  all_vat_amount: [{ value: 0, disabled: true }],
		  payment_amount_including_vat: [{ value: 0, disabled: true }],
		  invoice_note: ['', Validators.maxLength(255)],
		  allocation_number: ['0'],
		  send_to_customer: [false],
		  customer_name: [''],
		  customer_id_number: [''],
		  customer_vat_number: [''],
		});

		this.updateFormStateForDocumentType(this.documentType);
	}

	/** Document types that show the allocation number option (request / without request). */
	private static readonly ALLOCATION_DOCUMENT_TYPES = ['305', '320', '332', '0'];

	/**
	 * Whether to show the allocation number section. Uses documentType or form head.invoice_type
	 * (fallback when documentType is not set yet, e.g. edit mode during load). Normalizes type
	 * to string so number vs string from API does not hide the section.
	 */
	get showAllocationSection(): boolean {
		const type =
			this.documentType ??
			this.documentForm?.get('head')?.get('invoice_type')?.value ??
			null;
		if (type == null) return false;
		const normalized = String(type).trim();
		return DocumentComponent.ALLOCATION_DOCUMENT_TYPES.includes(normalized);
	}

	/** Getter for the 'head' FormGroup. */
	get headFormGroup(): FormGroup {
		return this.documentForm.get('head') as FormGroup;
	}

	/** Getter for the 'lines' FormArray. */
	get linesFormArray(): FormArray {
		return this.documentForm.get('lines') as FormArray;
	}

	/** Getter for the 'payments' FormGroup. */
	get paymentsFormGroup(): FormGroup {
		return this.documentForm.get('payments') as FormGroup;
	}

	/**
	 * Triggers initial checks and data fetches based on the initial form state,
	 * such as fetching company/customer details or VAT percentage.
	 */
	triggerInitialChecks(): void {
		if (this.headFormGroup.get('company_id')?.enabled && this.headFormGroup.get('company_id')?.value) {
		  this.handleCompanyChange(this.headFormGroup.get('company_id')?.value);
		}
		if (this.headFormGroup.get('invoice_date')?.enabled && this.headFormGroup.get('invoice_date')?.value) {
		  this.handleDateChange(this.headFormGroup.get('invoice_date')?.value);
		}
		 if (this.headFormGroup.get('customer_id')?.enabled && this.headFormGroup.get('customer_id')?.value) {
		  this.handleCustomerChange(this.headFormGroup.get('customer_id')?.value);
		}
		if (this.isVatApplicable && this.headFormGroup.get('vat_list_id')?.enabled && this.headFormGroup.get('invoice_date')?.enabled) {
			this.getVatPercentageByVatListIdAndDate();
		}
	}

	onTaxAuthorityDialogConfirm(): void {
		this.service.ConnectWithTaxGovernment(this.headFormGroup.get('company_id')?.value).subscribe((res: any) => {
			this.taxAutorityDialogVisible = false;
			window.open(res.url, '_blank');
		});
	};

	/**
	 * Handles changes to the selected company. Fetches company details and updates related form fields or state.
	 * @param companyId The ID of the selected company.
	 */
	handleCompanyChange(companyId: number | null): void {
if (companyId !== null && companyId !== undefined) {
			if (this.isVatApplicable) {
				this.getVatPercentageByVatListIdAndDate();
			}
		this.service.getCompanyInfoById(companyId).subscribe({
			next: (company: Company) => {
// Check if tax authority connection is missing or expired
				const isTokenExpired = company.refresh_token_expiration === null || 
					(company.refresh_token_expiration && new Date(company.refresh_token_expiration) < new Date());
				
				this.taxAutorityMissing = isTokenExpired;
				
				// Debug logging
				// Set VAT Type based on company's vat_list_id
				// Create a mapping of company_type_id to vat_list_id
				// Based on the API response you showed: company_type_id 3 -> vat_list_id 9
				const companyTypeToVatListMapping: { [key: number]: number } = {
					3: 9, // جمعيه (association) -> معفي من الضريبه (Exempt from tax)
					// Add more mappings as needed
				};
				
				if (company.company_type_id && companyTypeToVatListMapping[company.company_type_id]) {
					const companyVatListId = companyTypeToVatListMapping[company.company_type_id];
					const currentVatListId = this.headFormGroup.get('vat_list_id')?.value;
					
					// Update the VAT Type form control
					this.headFormGroup.patchValue({
						vat_list_id: companyVatListId
					});
					
					// Verify the change was applied
					const newVatListId = this.headFormGroup.get('vat_list_id')?.value;
					// Trigger VAT percentage fetch with the new vat_list_id
					if (this.isVatApplicable) {
						this.getVatPercentageByVatListIdAndDate();
					}
				} else {
					}
				
				this.headFormGroup.patchValue({});
				this.cdr.detectChanges();
			},
				error: (error) => {
					console.error('[DEBUG/DocumentComponent] Error fetching company details:', error);
					this.messageService.add({
						severity: 'error',
						summary: this.translate.instant('notifications.errorSummary'),
						detail: this.translate.instant('notifications.loadEntityErrorDetail', { entity: this.translate.instant('allDocuments.columns.company_name') })
					});
				}
			});
		} else {
			this.isMonthClosed = false;
			this.headFormGroup.get('vat_percentage')?.setValue(0);
		}
	}

	/**
	 * Handles changes to the selected customer. Fetches customer details and updates related form fields.
	 * @param customerId The ID of the selected customer.
	 */
	handleCustomerChange(customerId: number | null): void {
		if (customerId) {
			this.service.getCustomerById(customerId).subscribe({
				next: (customer: Customer) => {
this.documentForm.patchValue({
						customer_name: customer.customer_name_en,
						customer_id_number: customer.id_number,
						customer_vat_number: customer.customer_vat_number
					});
					this.cdr.detectChanges();
					this.lastCheckedCustomer = customer;
				},
				error: (error) => {
					console.error('[DEBUG/DocumentComponent]: Error fetching customer details:', error);
					this.messageService.add({
						severity: 'error',
						summary: this.translate.instant('notifications.errorSummary'),
						detail: this.translate.instant('notifications.loadEntityErrorDetail', { entity: this.translate.instant('allDocuments.columns.customer_name') })
					});
				}
			});
		 } else {
			this.documentForm.patchValue({
				customer_name: '',
				customer_id_number: '',
				customer_vat_number: ''
			});
		 }
	}

	/**
	 * Handles changes to the document date. Triggers VAT percentage fetch if applicable.
	 * @param date The selected date string (YYYY-MM-DD).
	 */
	handleDateChange(date: string | null): void {
if (date) {
			if (this.isVatApplicable) {
				this.getVatPercentageByVatListIdAndDate();
			}
		} else {
			this.isMonthClosed = false;
			this.headFormGroup.get('vat_percentage')?.setValue(0);
		}
	}

	/**
	 * Handles changes to the selected VAT type. Triggers VAT percentage fetch and recalculation if applicable.
	 * @param vatTypeId The ID of the selected VAT type.
	 */
	handleVatTypeChange(vatTypeId: number | null): void {
		if (!this.isVatApplicable) return;
if (vatTypeId !== null) {
			this.getVatPercentageByVatListIdAndDate();
		} else {
			this.headFormGroup.get('vat_percentage')?.setValue(0);
// Update all existing line items' VAT rate to 0
			this.updateAllLineItemsVatRate();
			this.recalculateAllLinesAndSummary();
		}
	}

	/**
	 * Updates all existing line items' VAT rate to match the current document VAT percentage.
	 * This ensures that when VAT type changes, all line items use the new VAT rate.
	 */
	updateAllLineItemsVatRate(): void {
		if (!this.showLinesSection) return;
		
		const currentVatPercentage = this.headFormGroup.get('vat_percentage')?.value ?? 0;
		
this.linesFormArray.controls.forEach((lineControl: AbstractControl) => {
			if (lineControl instanceof FormGroup) {
				const lineGroup = lineControl as FormGroup;
				// Update the vat_rate field for this line item
				lineGroup.get('vat_rate')?.setValue(currentVatPercentage, { emitEvent: false });
				
				// Force recalculation of this line item with the new VAT rate
				// This will update vat_amount and total_amount based on the new rate
				const quantity = lineGroup.get('quantity')?.value || 0;
				const pricePerUnit = lineGroup.get('price_per_unit')?.value || 0;
				const discount = lineGroup.get('discount')?.value || 0;
				const isVatIncluded = lineGroup.get('isVatIncluded')?.value || false;
				
				let lineSubTotal = quantity * pricePerUnit;
				let lineDiscountAmount = discount;
				let lineAmountAfterDiscount = lineSubTotal - lineDiscountAmount;
				let lineVatAmount = 0;
				let lineTotalAmount = 0;
				
				// Check if this is a tax-exempt item
				const isTaxExempt = currentVatPercentage === 0;
				
				if (isTaxExempt) {
					// For tax-exempt items, no VAT calculation
					lineVatAmount = 0;
					lineTotalAmount = lineAmountAfterDiscount;
				} else {
					// For taxable items, apply VAT calculation
					if (isVatIncluded) {
						const basePrice = lineAmountAfterDiscount / (1 + currentVatPercentage / 100);
						lineVatAmount = lineAmountAfterDiscount - basePrice;
						lineTotalAmount = lineAmountAfterDiscount;
					} else {
						lineVatAmount = lineAmountAfterDiscount * (currentVatPercentage / 100);
						lineTotalAmount = lineAmountAfterDiscount + lineVatAmount;
					}
				}

				lineGroup.patchValue({
					sub_total: lineSubTotal,
					vat_amount: lineVatAmount,
					total_amount: lineTotalAmount
				}, { emitEvent: true }); // Emit event to trigger line item recalculation
			}
		});
	}

	/**
	 * Fetches the applicable VAT percentage from the backend based on the selected VAT type and date.
	 * Updates the `vat_percentage` form control and triggers recalculation.
	 */
	getVatPercentageByVatListIdAndDate(): void {
		if (!this.isVatApplicable) {
this.headFormGroup.patchValue({ vat_percentage: 0 });
			// Update all existing line items' VAT rate to 0
			this.updateAllLineItemsVatRate();
			this.recalculateAllLinesAndSummary();
			return;
		}
		const vatListId = this.headFormGroup.get('vat_list_id')?.value;
		const invoiceDate = this.headFormGroup.get('invoice_date')?.value;
		const currentVatPercentage = this.headFormGroup.get('vat_percentage')?.value;

		if (!vatListId || !invoiceDate) {
this.headFormGroup.patchValue({'vat_percentage': 0});
			if (currentVatPercentage !== 0) {
				// Update all existing line items' VAT rate to 0
				this.updateAllLineItemsVatRate();
				this.recalculateAllLinesAndSummary();
			}
			return;
		}
this.spinner.show();
		this.service.getVatPercentageByVatListIdAndDate(vatListId, invoiceDate).subscribe({
			next: (res: any[]) => {
				const newVatPercentage = (Array.isArray(res) && res.length > 0 && res[0]?.vat_percentage)
					? parseFloat(res[0].vat_percentage) || 0
					: 0;
this.headFormGroup.patchValue({'vat_percentage': newVatPercentage});
				this.spinner.hide();
				if (currentVatPercentage !== newVatPercentage) {
					// Update all existing line items' VAT rate to match the new percentage
					this.updateAllLineItemsVatRate();
					this.recalculateAllLinesAndSummary();
				}
				this.cdr.detectChanges();
			},
			error: (error) => {
				console.error('[DEBUG/DocumentComponent] Error fetching VAT percentage:', error);
				this.messageService.add({
					severity: 'error',
					summary: this.translate.instant('notifications.errorSummary'),
					detail: this.translate.instant('notifications.document.vatLoadErrorDetail')
				});
				this.headFormGroup.patchValue({'vat_percentage': 0});
				if (currentVatPercentage !== 0) {
					// Update all existing line items' VAT rate to 0
					this.updateAllLineItemsVatRate();
					this.recalculateAllLinesAndSummary();
				}
				this.cdr.detectChanges();
			},
		});
	}

	/**
	 * Handles the summary data emitted by the document lines component.
	 * Updates the main form's summary fields.
	 * @param summaryData An object containing calculated totals from the lines.
	 */
	onLinesSummaryChanged(summaryData: any): void {
		if (!this.showLinesSection) return;
this.documentForm.patchValue({
			amount_before_discount: summaryData.amount_before_discount,
			all_discount: summaryData.all_discount,
			payment_amount: summaryData.payment_amount,
			all_vat_amount: summaryData.all_vat_amount,
			payment_amount_including_vat: summaryData.payment_amount_including_vat
		}, { emitEvent: false });
		this.cdr.detectChanges();
	}

	/**
	 * Handles the `totalsChanged` event emitted by the `DocumentPaymentsComponent`.
	 * Updates the summary fields within the 'payments' FormGroup and potentially the main total.
	 * @param totals The calculated payment totals object from the child component.
	 */
	onPaymentTotalsChanged(totals: PaymentTotals): void {
if (this.paymentsFormGroup) {
		  this.paymentsFormGroup.patchValue({
			cash_amount: totals.cash,
			checks_amount: totals.checks,
			bank_payment_amount: totals.bank,
			credit_amount: totals.credit,
			deduction_amount: totals.deduction ?? 0
		  }, { emitEvent: false });

		  this.cdr.detectChanges();
		} else {
		  console.warn('[DEBUG/DocumentComponent] onPaymentTotalsChanged called but paymentsFormGroup is missing.');
		}
	  }

	  /**
	   * Handles the `paymentAdded` event emitted by the `DocumentPaymentsComponent`.
	   * @param method The payment method details that were added.
	   */
	  onPaymentAdded(method: PaymentMethod | undefined): void {
}

	  /**
	   * Handles the `paymentRemoved` event emitted by the `DocumentPaymentsComponent`.
	   * @param event Details about the removed payment line (type and index).
	   */
	  onPaymentRemoved(event: { type: keyof PaymentTotals | string, index: number }): void {
}

	/**
	 * Recalculates all line totals (subtotal, VAT, total) based on quantity, price, discount, and VAT settings.
	 * Also recalculates the overall document summary totals based on the lines.
	 * Resets summary if lines are not shown.
	 */
	recalculateAllLinesAndSummary(): void {
		// DISABLED: This old method causes floating-point precision errors
		// The new calculation methods in DocumentLineComponent and DocumentLinesComponent handle this correctly
		return;
		
		if (!this.showLinesSection) {
			this.documentForm.patchValue({
				amount_before_discount: 0,
				all_discount: 0,
				payment_amount: 0,
				all_vat_amount: 0,
				payment_amount_including_vat: 0
			}, { emitEvent: false });
this.cdr.detectChanges();
			return;
		}
const vatPercentage = this.isVatApplicable ? (this.headFormGroup.get('vat_percentage')?.value ?? 0) : 0;
		let totalAmountBeforeDiscount = 0;
		let totalDiscount = 0;
		let totalAmountAfterDiscount = 0;
		let totalVatAmount = 0;
		let totalAmountIncludingVat = 0;
		this.linesFormArray.controls.forEach((lineControl: AbstractControl) => {
			if (lineControl instanceof FormGroup) {
				const lineGroup = lineControl as FormGroup;
				const quantity = lineGroup.get('quantity')?.value || 0;
				const pricePerUnit = lineGroup.get('price_per_unit')?.value || 0;
				const discount = lineGroup.get('discount')?.value || 0;
				const discountType = lineGroup.get('discountType')?.value || 'amount';
				const isVatIncluded = lineGroup.get('isVatIncluded')?.value || false;
				
				// Calculate line total before discount
				let lineSubTotalBeforeDiscount = quantity * pricePerUnit;
				let lineDiscountAmount = 0;
				
				// Calculate discount amount based on type
				if (discountType === 'percentage') {
					const validDiscountPercent = Math.min(discount, 100);
					lineDiscountAmount = lineSubTotalBeforeDiscount * (validDiscountPercent / 100);
				} else {
					lineDiscountAmount = Math.min(discount, lineSubTotalBeforeDiscount);
					if (lineSubTotalBeforeDiscount <= 0) lineDiscountAmount = 0;
				}
				lineDiscountAmount = Math.max(0, lineDiscountAmount);
				
				// Calculate line subtotal after discount
				let lineSubTotal = lineSubTotalBeforeDiscount - lineDiscountAmount;
				let lineAmountAfterDiscount = lineSubTotal;
				let lineVatAmount = 0;
				let lineTotalAmount = 0;
				
				// Check if this is a tax-exempt item
				const isTaxExempt = vatPercentage === 0;
				
				if (isTaxExempt) {
					// For tax-exempt items, no VAT calculation
					lineVatAmount = 0;
					lineTotalAmount = lineAmountAfterDiscount;
				} else if (isVatIncluded) {
					// When VAT is included, extract net price and calculate correct VAT
					const netPrice = lineAmountAfterDiscount / (1 + vatPercentage / 100);
					lineVatAmount = netPrice * (vatPercentage / 100);
					lineTotalAmount = lineAmountAfterDiscount; // Total is just the subtotal, VAT is already included in the original price
				} else {
					// When VAT is excluded, apply VAT calculation
					lineVatAmount = lineAmountAfterDiscount * (vatPercentage / 100);
					lineTotalAmount = lineAmountAfterDiscount + lineVatAmount;
				}

				// Round to 2 decimal places to avoid floating-point precision errors
				const roundedLineSubTotal = Math.round(lineSubTotal * 100) / 100;
				const roundedLineVatAmount = Math.round(lineVatAmount * 100) / 100;
				const roundedLineTotalAmount = Math.round(lineTotalAmount * 100) / 100;

				lineGroup.patchValue({
					sub_total: roundedLineSubTotal,
					vat_rate: vatPercentage,
					vat_amount: roundedLineVatAmount,
					total_amount: roundedLineTotalAmount
				}, { emitEvent: false });

				totalAmountBeforeDiscount += lineSubTotalBeforeDiscount;
				totalDiscount += lineDiscountAmount;
				totalAmountAfterDiscount += lineAmountAfterDiscount;
				totalVatAmount += lineVatAmount;
				totalAmountIncludingVat += lineTotalAmount;
			}
		});

		this.documentForm.patchValue({
			amount_before_discount: totalAmountBeforeDiscount,
			all_discount: totalDiscount,
			payment_amount: totalAmountAfterDiscount,
			all_vat_amount: totalVatAmount,
			payment_amount_including_vat: totalAmountIncludingVat
		}, { emitEvent: false });
this.cdr.detectChanges();
	}

	/**
	 * Initiates the save process for the document (without confirmation).
	 * Calls the common save/confirm handler.
	 */
	saveDocument(): void {
		this.handleSaveOrConfirm('save');
	}

	/**
	 * Initiates the confirmation process for the document.
	 * Calls the common save/confirm handler.
	 */
	confirmDocument(): void {
		this.handleSaveOrConfirm('confirm');
	}

	/**
	 * Central handler for both 'save' and 'confirm' actions.
	 * Performs validation, prepares data, and calls the appropriate API endpoint.
	 * @param action The type of action: 'save' or 'confirm'.
	 * @private
	 */
	private handleSaveOrConfirm(action: 'save' | 'confirm'): void {
		this.formSubmitted = true;
		
		// Validate company type and invoice type compatibility
		if (!this.validateCompanyInvoiceTypeAccess()) {
			this.isSubmitting = false;
			return;
		}
		
		// Set confirm process flag to prevent flag resets during form validation
		if (action === 'confirm') {
			// Use a global flag that line components can check
			(window as any).isInConfirmProcess = true;
			// Reset the flag after a delay
			setTimeout(() => {
				(window as any).isInConfirmProcess = false;
				}, 1000);
		}
		
		this.markFormGroupTouched(this.documentForm);
		this.preparedPayloadForDebug = null;

		if (this.isSubmitting) {
			console.warn(`[DEBUG/DocumentComponent] Submission already in progress. Aborting.`);
			return;
		}
		this.isSubmitting = true;

		if (this.documentForm.invalid) {
			const invalids = this.getInvalidControlsRecursive(this.documentForm);
			console.warn('[DEBUG/DocumentComponent] Form is invalid. Aborting.', invalids);

			// Highlight invalid product lines
			if (invalids.includes('lines')) {
				const linesArray = this.linesFormArray;
				linesArray.controls.forEach(ctrl => {
					ctrl.markAllAsTouched();
					ctrl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
				});
			}

			const linesCreditArray = this.paymentsFormGroup.get('linesCredit') as FormArray;
			if (linesCreditArray) {
				linesCreditArray.controls.forEach((ctrl, idx) => {
					const group = ctrl as FormGroup;
					});
			} else {
				}

			this.messageService.add({
				severity: 'warn',
				summary: this.translate.instant('notifications.errorSummary'),
				detail: this.translate.instant('notifications.document.validationErrorDetail')
			});
			this.scrollToFirstInvalidControl();
			this.isSubmitting = false;
			return;
		}

		if (
			action === 'confirm' &&
			this.showPaymentsSection &&
			this.paymentsFormGroup
		) {
			this.noPaymentMethods = false;
			const payments = this.paymentsFormGroup.getRawValue();
			const hasPayment =
				(payments.linesCash && payments.linesCash.length > 0) ||
				(payments.linesChecks && payments.linesChecks.length > 0) ||
				(payments.linesBankPayment && payments.linesBankPayment.length > 0) ||
				(payments.linesCredit && payments.linesCredit.length > 0);

			if (!hasPayment) {
				this.messageService.add({
					severity: 'warn',
					summary: this.translate.instant('notifications.errorSummary'),
					detail: this.translate.instant('notifications.document.paymentRequiredForConfirmation')
						|| 'At least one payment line is required to confirm the document.'
				});
				this.noPaymentMethods = true;
				this.scrollToFirstInvalidControl();
				this.isSubmitting = false;
				return;
			}

			if (this.showLinesSection) {
				const totalProducts = parseFloat(Number(this.documentForm.get('payment_amount_including_vat')?.value || 0).toFixed(2));
				const totalPayments = parseFloat((
					(Number(payments.cash_amount) || 0) +
					(Number(payments.checks_amount) || 0) +
					(Number(payments.bank_payment_amount) || 0) +
					(Number(payments.credit_amount) || 0) +
					(Number(payments.deduction_amount) || 0)
				).toFixed(2));

				if (totalProducts !== totalPayments) {
					this.messageService.add({
						severity: 'warn',
						summary: this.translate.instant('notifications.errorSummary'),
						detail: this.translate.instant('notifications.document.paymentTotalMismatch') ||
							'Total payments must equal total products (including VAT) to confirm the document.'
					});
					this.noPaymentMethods = true;
					this.scrollToFirstInvalidControl();
					this.isSubmitting = false;
					return;
				}
			}
		}

		if (action === 'confirm') {
			const year = new Date().getFullYear();
			this.service.GetYearlyMaxAmount(year).subscribe((maxAmountRes: any) => {
				let maxAmount = 10000;
				if (Array.isArray(maxAmountRes) && maxAmountRes.length > 0 && maxAmountRes[0]?.maxAmount) {
					maxAmount = Number(maxAmountRes[0].maxAmount) || 10000;
				}
				this.yearlyMaxAmount = maxAmount;
				// Per-invoice threshold: ₪10,000 before 1 June 2026; ₪5,000 from 1 June 2026 onward.
				const perInvoiceThreshold = new Date() >= new Date(2026, 5, 1) ? 5000 : 10000;
				// Use amount before VAT (not including VAT) and restrict to Tax Invoice (305) / Tax Invoice+Receipt (320).
				const amountBeforeVat = this.documentForm.get('amount_before_discount')?.value || 0;
				const isEligibleDocType = ['305', '320'].includes(String(this.documentType));
				const customer = this.lastCheckedCustomer;
				const customerType = customer?.customer_type;
				const customerId = customer?.customer_id;

				if (isEligibleDocType && amountBeforeVat > perInvoiceThreshold) {
					// Check if user selected "بدون رقم تخصيص" (Without allocation number)
					const allocationNumber = this.documentForm.get('allocation_number')?.value;
					
					if (allocationNumber === 0 || allocationNumber === '0') {
						// Skip Tax Authority modal and proceed directly
						this.handleValidForm(action);
						return;
					}
					
					if ( this.taxAutorityMissing ) {
						this.taxAutorityDialogVisible = true;
						return;
					}
					if (customerType === 6) {
						const customerVatNumber = Number(customer?.customer_vat_number);
						if (!customerVatNumber) {
							this.openPrimeDialog('vat', 'edit', customer, action);
							return;
						}
					}
					if (customerType === 5) {
						const customerIdNumber = Number(customer?.id_number);
						if (!customerIdNumber) {
							this.openPrimeDialog('id', 'edit', customer, action);
							return;
						}
					}
				}
				this.handleValidForm(action);
			}, error => {
				console.error('[DEBUG/DocumentComponent] Error fetching yearly max amount:', error);
				this.messageService.add({
					severity: 'error',
					summary: this.translate.instant('notifications.errorSummary'),
					detail: 'Failed to retrieve yearly maximum amount.'
				});
				this.isSubmitting = false;
			});
		} else {
			this.handleValidForm(action);
		}
	}

	/**
	 * Handles the process after the form is deemed valid for saving or confirming.
	 * Prepares the payload and makes the API call.
	 * @param action The type of action: 'save' or 'confirm'.
	 * @private
	 */
	private handleValidForm(action: 'save' | 'confirm'): void {
		const requiresCustomerValidation = false;
		if (requiresCustomerValidation) {
			console.warn('[DEBUG/DocumentComponent] Customer validation required but not implemented.');
		}
		if (this.paymentsComponent && this.paymentsFormGroup) {
			this.paymentsFormGroup.get('linesCash')?.setValue(
				this.paymentsComponent.linesCash?.getRawValue() || []
			);
			this.paymentsFormGroup.get('linesChecks')?.setValue(
				this.paymentsComponent.linesChecks?.getRawValue() || []
			);
			this.paymentsFormGroup.get('linesBankPayment')?.setValue(
				this.paymentsComponent.linesBankPayment?.getRawValue() || []
			);
			this.paymentsFormGroup.get('linesCredit')?.setValue(
				this.paymentsComponent.linesCredit?.getRawValue() || []
			);
		}

		const formData = this.documentForm.getRawValue();
		const payloadData: any = {
			invoice_type: Number(formData.head.invoice_type),
			document_language: formData.head.document_language,
			allocation_number: Number(formData.allocation_number), // convert to number for API
			send_to_customer: formData.send_to_customer || false,
			invoice_date: formData.head.invoice_date,
			invoice_note: formData.invoice_note,
			customer_id: formData.head.customer_id,
			company_id: Number(formData.head.company_id ?? formData.company_id ?? 0) || null,
			vat_list_id: this.isVatApplicable ? formData.head.vat_list_id : null,
			vat_percentage: this.isVatApplicable ? Number(formData.head.vat_percentage) : 0,
			currency: formData.head.currency,
			confirm: action === 'confirm' ? 1 : 0,
			merchant_id: formData.merchant_id,
			created_by: formData.created_by,
			customer_name: formData.customer_name,
			customer_id_number: formData.customer_id_number,
			customer_vat_number: formData.customer_vat_number,
			amount_before_discount: this.showLinesSection ? (Number(formData.amount_before_discount) || 0) : 0,
			all_discount: this.showLinesSection ? (Number(formData.all_discount) || 0) : 0,
			payment_amount: this.showLinesSection ? (Number(formData.payment_amount) || 0) : (this.showPaymentsSection ? (Number(formData.payment_amount_including_vat) || 0) : 0),
			all_vat_amount: this.showLinesSection && this.isVatApplicable ? (Number(formData.all_vat_amount) || 0) : 0,
			payment_amount_including_vat: Number(formData.payment_amount_including_vat) || 0,
			cash_amount: this.showPaymentsSection ? (Number(formData.payments?.cash_amount) || 0) : 0,
			checks_amount: this.showPaymentsSection ? (Number(formData.payments?.checks_amount) || 0) : 0,
			bank_payment_amount: this.showPaymentsSection ? (Number(formData.payments?.bank_payment_amount) || 0) : 0,
			credit_amount: this.showPaymentsSection ? (Number(formData.payments?.credit_amount) || 0) : 0,
			total_amount: Number(formData.payment_amount_including_vat) || 0,
			lines: this.showLinesSection ? formData.lines.map((line: any) => ({
				index: line.index,
				product_id: line.product_id,
				category: line.category,
				measure_unit_description: line.measure_unit_description,
				measurement_unit_id: line.measurement_unit_id,
				description: line.description,
				catalog_id: line.catalog_id,
				item_name: line.item_name,
				quantity: Number(line.quantity) || 0,
				price_per_unit: Number(line.price_per_unit) || 0,
				discount: Number(line.discount) || 0,
				sub_total: Number(line.sub_total) || 0,
				vat_rate: this.isVatApplicable ? Number(line.vat_rate) : 0,
				vat_amount: this.isVatApplicable ? (Number(line.vat_amount) || 0) : 0,
				total_amount: Number(line.total_amount) || 0,
				isVatIncluded: line.isVatIncluded,
				isPresenting: line.isPresenting,
				...(this.mode === 'edit' && line.sold_item_id && { sold_item_id: line.sold_item_id })
			})) : [],
			linesCash: this.showPaymentsSection
				? (formData.payments?.linesCash || []).map((line: CashPaymentLine) => ({
					id: line.id ?? line.id ?? undefined,
					date: this.datePipe.transform(line.date, 'yyyy-MM-dd'),
					amount: Number(line.amount),
					payment_type: 1
				}))
				: [],
			linesChecks: this.showPaymentsSection
				? (formData.payments?.linesChecks || []).map((line: any) => ({
					id: line.id ?? undefined,
					date: this.datePipe.transform(line.date, 'yyyy-MM-dd'),
					bank: line.bank ?? '',
					branch: line.branch ?? '',
					account: line.account ?? '',
					reference: line.check ?? line.reference ?? '', // Transform 'check' to 'reference'
					amount: Number(line.amount),
					payment_type: 2
				}))
				: [],
			linesBankPayment: this.showPaymentsSection
				? (formData.payments?.linesBankPayment || []).map((line: any) => ({
					id: line.id ?? undefined,
					date: this.datePipe.transform(line.date, 'yyyy-MM-dd'),
					amount: Number(line.amount),
					account: line.account ?? '',
					bank: line.bank ?? '',
					payment_type: 4
				}))
				: [],
			linesCredit: this.showPaymentsSection
				? (formData.payments?.linesCredit || []).map((line: CreditCardPaymentLine) => ({
					id: line.id ?? undefined,
					date: this.datePipe.transform(line.date, 'yyyy-MM-dd'),
					amount: Number(line.amount),
					credit_card_code: String(line.credit_card_code ?? '').slice(0, 10),
					credit_card_name: String(line.credit_card_name ?? '').slice(0, 50),
					credit_type: String(line.credit_type ?? '').slice(0, 10),
					account: String(line.account ?? ''),
					account_number: String(line.account ?? ''),
					payment_type: 3
				}))
				: [],
			linesDeduction: this.showPaymentsSection
				? (formData.payments?.linesDeduction || []).map((line: any) => ({
					id: line.id ?? undefined,
					amount: Number(line.amount),
					payment_type: 5
				}))
				: [],
		};

		if (typeof payloadData.deduction_from_source !== 'number') {
			payloadData.deduction_from_source = 0;
		}
		if (payloadData.vat_list_id === null || payloadData.vat_list_id === undefined) {
			payloadData.vat_list_id = 8;
		}
		if (formData.payments?.linesDeduction && Array.isArray(formData.payments.linesDeduction)) {
			payloadData.deduction_from_source = formData.payments.linesDeduction.reduce((sum: number, line: any) => {
				return sum + (Number(line.amount) || 0);
			}, 0);
		}

		if (!Array.isArray(payloadData.linesCash)) payloadData.linesCash = [];
		if (!Array.isArray(payloadData.linesChecks)) payloadData.linesChecks = [];
		if (!Array.isArray(payloadData.linesBankPayment)) payloadData.linesBankPayment = [];
		if (!Array.isArray(payloadData.linesCredit)) payloadData.linesCredit = [];

		if (!this.showLinesSection) {
			payloadData.lines = [];
		}

		this.preparedPayloadForDebug = payloadData;
		this.cdr.detectChanges();
		this.spinner.show();

		let apiCall$;
		const isEditing = !!this.documentId;
		const docType = String(payloadData.invoice_type);

		const endpointMap = {
			'0': {
				save: (data: any) => isEditing
					? this.service.SaveUpdatedInvoice(this.documentId!, data)
					: this.service.SaveInvoice(data),
				confirm: (data: any) => isEditing
					? this.service.SaveUpdatedInvoice(this.documentId!, data)
					: this.service.SaveInvoice(data),
			},
			'100': {
				save: (data: any) => isEditing
					? this.service.SaveUpdatedInvoice(this.documentId!, data)
					: this.service.SaveInvoice(data),
				confirm: (data: any) => isEditing
					? this.service.SaveUpdatedInvoice(this.documentId!, data)
					: this.service.SaveInvoice(data),
			},
			'200': {
				save: (data: any) => isEditing
					? this.service.SaveUpdatedInvoice(this.documentId!, data)
					: this.service.SaveInvoice(data),
				confirm: (data: any) => isEditing
					? this.service.SaveUpdatedInvoice(this.documentId!, data)
					: this.service.SaveInvoice(data),
			},
			'305': {
				save: (data: any) => isEditing
					? this.service.SaveUpdatedInvoice(this.documentId!, data)
					: this.service.SaveInvoice(data),
				confirm: (data: any) => isEditing
					? this.service.SaveUpdatedInvoice(this.documentId!, data)
					: this.service.SaveInvoice(data),
			},
			'320': {
				save: (data: any) => isEditing
					? this.service.updateInvoiceAndReceipt(this.documentId!, data)
					: this.service.createInvoiceAndReceipt(data),
				confirm: (data: any) => isEditing
					? this.service.updateInvoiceAndReceipt(this.documentId!, data)
					: this.service.createInvoiceAndReceipt(data)
			},
			'330': {
				save: (data: any) => isEditing
					? this.service.updateInvoiceAndReceipt(data, this.documentId!)
					: this.service.createInvoiceAndReceipt(data),
				confirm: (data: any) => isEditing
					? this.service.updateInvoiceAndReceipt(data, this.documentId!)
					: this.service.createInvoiceAndReceipt(data)
			},
			'400': {
				save: (data: any) => this.service.SaveReceipt(data),
				confirm: (data: any) => this.service.SaveReceipt(data)
			}
		};

		const endpoints = endpointMap[docType as keyof typeof endpointMap];

		if (endpoints && endpoints[action]) {
			apiCall$ = endpoints[action](payloadData);
		} else {
			// Default or fallback if needed, ensure it aligns with backend logic
			console.warn(`[DEBUG/DocumentComponent] No specific endpoint mapping found for type ${docType} and action ${action}. Using default.`);
			// Decide on a sensible default, maybe just the save action?
			apiCall$ = isEditing
				? this.service.updateInvoiceAndReceipt(this.documentId!, payloadData)
				: this.service.createInvoiceAndReceipt(payloadData);
		}

		apiCall$.subscribe({
			next: (response: any) => {
				this.spinner.hide();
				this.isSubmitting = false;
				// Check for specific allocation denial message
				const allocationDenialMessage = "הריני להודיעך כי מבדיקה שנערכה ביחס לבקשתך לקבל מספר הקצאה לחשבונית המס";
				const responseMessage = response?.messageHe || response?.messageAr || response?.messageEn || response?.message || '';
				
				if (responseMessage && (responseMessage.includes(allocationDenialMessage) || responseMessage.includes("מספר הקצאה לחשבונית המס"))) {
					// Mark this invoice as having allocation denial
					// Check both direct response properties and nested data properties
					const invoiceId = response?.invoice_id || response?.data?.invoice_id;
					const invoiceUuid = response?.invoice_uuid || response?.data?.invoice_uuid;
					
					if (invoiceId) {
						this.allocationDenialService.markAsAllocationDenial(invoiceId);
					} else if (invoiceUuid) {
						this.allocationDenialService.markAsAllocationDenial(invoiceUuid);
					} else {
						}
					
										// Don't show the toast if this flow was a suppressed invoice 405
										try {
											const suppressed = (response && response._suppressedInvoice405) || (window as any).__suppressInvoice405Swal;
											if (!suppressed) {
												this.messageService.add({
													severity: 'warn',
													summary: 'התראה',
													detail: responseMessage,
													life: 10000
												});
											} else {
												}
										} catch (e) {
											// On any error, fall back to showing the message
											this.messageService.add({
												severity: 'warn',
												summary: 'התראה',
												detail: responseMessage,
												life: 10000
											});
										}
					setTimeout(() => {
						// Navigate to All Documents and include the created invoice identifier so it opens automatically
						const invoiceUuid = response?.invoice_uuid || response?.data?.invoice_uuid || null;
						const invoiceId = response?.invoice_id || response?.data?.invoice_id || null;
						const query = invoiceUuid || invoiceId ? { showDocument: invoiceUuid || invoiceId } : {};
						this.router.navigate(['/dashboard/documents'], { queryParams: query });
					}, 3000); // Give user time to read the message
					this.formSubmitted = false;
					return;
				}
				
				if (response && (response.status === 200 || response.status === 201 || response.status === 204)) {
					this.messageService.add({
						severity: 'success',
						summary: this.translate.instant('notifications.successSummary'),
						detail: response.message || this.translate.instant('notifications.saveSuccessDetail', { action: action === 'save' ? (isEditing ? 'updated' : 'saved') : 'confirmed' })
					});
					if (response.data.confirm === 1) {
						this.router.navigate([`/dashboard/documents`], { queryParams: { showDocument: response.data.invoice_uuid } });
					} else {
						this.router.navigate([`/dashboard/documents`]);
					}
				} else if (response && response.statusCode === 400) {
					this.messageService.add({
						severity: 'error',
						summary: response.error,
						detail: response.message
					});
				} else {
					console.error(`[DEBUG/DocumentComponent] API Error (${action}) - Unexpected Success Status:`, response);
					this.messageService.add({
						severity: 'error',
						summary: response.error,
						detail: response.message
					});
				}
				this.formSubmitted = false;
			},
			error: (error) => {
				this.spinner.hide();
				this.isSubmitting = false;
				console.error(`[DEBUG/DocumentComponent] API Error (${action}):`, error);
				
				// Check for specific allocation denial message in error response
				const allocationDenialMessage = "הריני להודיעך כי מבדיקה שנערכה ביחס לבקשתך לקבל מספר הקצאה לחשבונית המס";
				const errorData = error?.error || error;
				const errorMessage = errorData?.messageHe || errorData?.messageAr || errorData?.messageEn || errorData?.message || '';
				
				if (errorMessage && (errorMessage.includes(allocationDenialMessage) || errorMessage.includes("מספר הקצאה לחשבונית המס"))) {
					// Mark this invoice as having allocation denial  
					// Note: In error scenarios, we might not have invoice_id, so we'll try to get it from form or context
					const formValue = this.documentForm.value;
					if (formValue?.invoice_id) {
						this.allocationDenialService.markAsAllocationDenial(formValue.invoice_id);
					} else {
						}
					
					this.messageService.add({
						severity: 'warn',
						summary: 'התראה',
						detail: errorMessage,
						life: 10000
					});
					setTimeout(() => {
						// In error scenarios try to pass invoice identifier from form if available
						const formValue = this.documentForm.value;
						const invoiceUuid = formValue?.invoice_uuid || formValue?.uuid || null;
						const invoiceId = formValue?.invoice_id || null;
						const query = invoiceUuid || invoiceId ? { showDocument: invoiceUuid || invoiceId } : {};
						this.router.navigate(['/dashboard/documents'], { queryParams: query });
					}, 3000); // Give user time to read the message
					this.formSubmitted = false;
					return;
				}
				
				this.messageService.add({
					severity: 'error',
					summary: error.error.error,
					detail: error.error.message
				});
			}
		});
	}

	/**
	 * Recursively marks all controls within a FormGroup or FormArray as touched.
	 * Useful for displaying validation errors on submit.
	 * @param formGroup The FormGroup or FormArray to mark as touched.
	 * @private
	 */
	private markFormGroupTouched(formGroup: FormGroup | FormArray): void {
		if (!formGroup || !formGroup.controls) {
			console.error("markFormGroupTouched called with invalid formGroup:", formGroup);
			return;
		}
		Object.values(formGroup.controls).forEach(control => {
			control.markAsTouched();
			control.updateValueAndValidity({ onlySelf: false, emitEvent: true });
			if (control instanceof FormGroup || control instanceof FormArray) {
				this.markFormGroupTouched(control);
			}
		});
	}

	/**
	 * Finds the first invalid control in the form and scrolls the view to it.
	 * Attempts to focus the control or a focusable element within it.
	 * @private
	 */
	private scrollToFirstInvalidControl(): void {
const invalidControlPaths = this.getInvalidControlsRecursive(this.documentForm);
if (invalidControlPaths.length === 0) {
return;
		}

		const firstErrorPath = invalidControlPaths[0].split('.');
let elementToScroll: HTMLElement | null = null;

		const findNestedControl = (root: HTMLElement, path: string[]): HTMLElement | null => {
			let current: HTMLElement | null = root;
			for (let i = 0; i < path.length - 1; i++) {
				const part = path[i];
				let next = current.querySelector(`[formGroupName="${part}"]`) as HTMLElement;
				if (!next) next = current.querySelector(`[formArrayName="${part}"]`) as HTMLElement;
				if (!next) {
					if (!isNaN(Number(part))) {
						next = current.querySelector(`[formGroupName="${part}"]`) as HTMLElement;
					}
				}
				if (!next) return null;
				current = next;
			}
			const controlName = path[path.length - 1];
			return current.querySelector(`[formControlName="${controlName}"]`) as HTMLElement;
		};

		if (firstErrorPath.length > 2) {
		
			elementToScroll = findNestedControl(this.el.nativeElement, firstErrorPath);
}

		if (!elementToScroll && firstErrorPath.length > 0) {
			const controlName = firstErrorPath[firstErrorPath.length - 1];
			const selector = `[formControlName="${controlName}"]`;
elementToScroll = this.el.nativeElement.querySelector(selector);
		}

		if (!elementToScroll && invalidControlPaths[0] === 'head.customer_id') {
elementToScroll = this.el.nativeElement.querySelector('app-customer-select');
		}

		if (elementToScroll) {
elementToScroll.scrollIntoView({ behavior: 'smooth', block: 'center' });

			setTimeout(() => {
if (typeof elementToScroll!.focus === 'function') {
					elementToScroll!.focus();
					const innerInput = elementToScroll!.querySelector('input, textarea, select');
					if (innerInput instanceof HTMLElement) {
innerInput.focus();
					} else {
}
				} else {
const focusableChild = elementToScroll!.querySelector<HTMLElement>(
						'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
					);
					if (focusableChild && typeof focusableChild.focus === 'function') {
focusableChild.focus();
					} else {
}
				}
			}, 150);
		} else {
			if (this.debug) console.warn(`[DEBUG/DocumentComponent] scrollToFirstInvalidControl: Could not find specific element for the first error path "${invalidControlPaths[0]}", scrolling to top of form.`);
			this.el.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}

	/**
	 * Recursively finds all invalid controls within a given AbstractControl (FormGroup, FormArray, FormControl).
	 * Returns an array of strings representing the paths to the invalid controls (e.g., 'head.customer_id', 'lines.0.quantity').
	 * Only includes controls that are touched, dirty, or pending validation.
	 * @param control The AbstractControl to check.
	 * @param prefix The current path prefix for nested controls.
	 * @returns An array of strings representing the paths to invalid controls.
	 * @private
	 */
	private getInvalidControlsRecursive(control: AbstractControl, prefix = ''): string[] {
let invalidControls: string[] = [];

		if (!control) {
return invalidControls;
		}

		if (control instanceof FormGroup || control instanceof FormArray) {
if (!control.controls) {
return invalidControls;
			}

			Object.keys(control.controls).forEach(key => {
				const childControl = control.get(key);
				if (childControl) {
					const controlPath = prefix ? `${prefix}.${key}` : key;
if (childControl.invalid && (childControl.touched || childControl.dirty || childControl.pending)) {
						if (!(childControl instanceof FormGroup || childControl instanceof FormArray)) {
invalidControls.push(controlPath);
						} else if (childControl.errors) {
invalidControls.push(controlPath);
						}
					}

					if (childControl instanceof FormGroup || childControl instanceof FormArray) {
						invalidControls = invalidControls.concat(this.getInvalidControlsRecursive(childControl, controlPath));
					}
				} else {
}
			});
		} else {
			if (control.invalid && (control.touched || control.dirty || control.pending)) {
invalidControls.push(prefix || 'root');
			}
		}

		const uniqueInvalids = [...new Set(invalidControls)];
return uniqueInvalids;
	}

	/**
	 * Navigates back to the documents page
	 */
	goBack(): void {
		this.router.navigate([`/dashboard/documents`]);
	}

	/**
	 * Handles changes to the selected document language.
	 * Updates the current language state and applies the translation.
	 * @param language The selected language code (e.g., 'en', 'he').
	 */
	handleLanguageChange(language: string): void {
		this.documentForm.patchValue({ document_language: language });
		this.cdr.detectChanges();
	}

	/**
	 * Callback function triggered when changes occur within the document lines component.
	 * Initiates a recalculation of the document summary.
	 */
	onLinesChanged(): void {
this.recalculateAllLinesAndSummary();
	}

	private openPrimeDialog(type: 'vat' | 'id', step: 'warning' | 'edit', customer: any, action: 'save' | 'confirm', resolve?: (result: any) => void) {
		this.dialogType = type;
		this.dialogStep = step;
		this.dialogVisible = true;
		this.dialogAction = action;
		this.dialogCustomerId = customer?.customer_id;
		this.dialogInputValue = type === 'vat' ? (customer?.customer_vat_number || '') : (customer?.id_number || '');
		this.dialogResolve = resolve || null;
		this.cdr.detectChanges();
	}

	// Call this on dialog confirm/cancel from template
	onDialogConfirm() {
		if (this.dialogStep === 'warning') {
			if (this.dialogType === 'vat') {
				this.documentForm.patchValue({ customer_vat_number: '999999998' });
			} else if (this.dialogType === 'id') {
				this.documentForm.patchValue({ id_number: '999999998' });
			}
			this.dialogVisible = false;
			this.handleValidForm(this.dialogAction); 
		} else if (this.dialogStep === 'edit') {
			const value = this.dialogInputValue;
			if (!value || value.trim() === '' || /^0+$/.test(value) || value.length < 9) {
				this.messageService.add({
					severity: 'warn',
					summary: this.translate.instant('notifications.errorSummary'),
					detail: this.dialogType === 'vat'
						? (this.curentLang === 'ar'
							? 'الرجاء إدخال رقم ضريبي صالح لا يقل عن 9 أرقام'
							: 'Please enter a valid VAT number with at least 9 digits')
						: (this.curentLang === 'ar'
							? 'الرجاء إدخال رقم هوية صالح لا يقل عن 9 أرقام'
							: 'Please enter a valid ID number with at least 9 digits')
				});
				return;
			}
			if (this.dialogType === 'vat') {
				this.documentForm.patchValue({ customer_vat_number: value });
				this.documentForm.patchValue({ customer_id_number: value });
				const model = {
					customer_vat_number: value,
					updated_by: this.documentForm.get('created_by')?.value
				};
				this.service.updateCustomersById(this.dialogCustomerId, model).subscribe(() => {
					this.dialogVisible = false;
					this.handleValidForm(this.dialogAction);
				}, () => {
					this.isSubmitting = false;
					this.dialogVisible = false;
					this.messageService.add({
						severity: 'error', summary: 'Error', detail: 'Failed to update customer VAT number.'
					});
				});
			} else if (this.dialogType === 'id') {
				this.documentForm.patchValue({ customer_vat_number: value });
				this.documentForm.patchValue({ customer_id_number: value });
				const model = {
					id_number: value,
					updated_by: this.documentForm.get('created_by')?.value
				};
				this.service.updateCustomersById(this.dialogCustomerId, model).subscribe(() => {
					this.dialogVisible = false;
					this.handleValidForm(this.dialogAction);
				}, () => {
					this.isSubmitting = false;
					this.dialogVisible = false;
					this.messageService.add({
						severity: 'error', summary: 'Error', detail: 'Failed to update customer ID number.'
					});
				});
			}
		}
	}

	onDialogCancel() {
		if (this.dialogStep === 'warning') {
			this.dialogStep = 'edit';
			this.cdr.detectChanges();
		} else {
			this.dialogVisible = false;
			this.isSubmitting = false;
		}
	}

	/**
	 * Validates that the selected company has access to the current invoice type
	 * @returns true if validation passes, false if it fails
	 * @private
	 */
	private validateCompanyInvoiceTypeAccess(): boolean {
		const selectedCompanyId = this.documentForm.get('head.company_id')?.value;
		const currentInvoiceType = this.documentForm.get('head.invoice_type')?.value;
		
		if (!selectedCompanyId || !currentInvoiceType) {
			return true;
		}

		// Get the selected company data
		const selectedCompany = this.companies?.find(c => c.company_id === selectedCompanyId);
		if (!selectedCompany) {
			return true;
		}

		// Check if company has companyType data
		if (!selectedCompany.companyType || !selectedCompany.companyType.invoice_types) {
			return true;
		}

		// Check if the current invoice type is in the allowed list
		const allowedInvoiceTypes = selectedCompany.companyType.invoice_types;
		const isAllowed = allowedInvoiceTypes.includes(parseInt(currentInvoiceType));

		if (!isAllowed) {
			console.warn('[DEBUG/DocumentComponent] Invoice type not allowed for this company:', {
				companyId: selectedCompanyId,
				companyName: selectedCompany.company_name_en,
				companyType: selectedCompany.companyType.type_name_en,
				requestedInvoiceType: currentInvoiceType,
				allowedInvoiceTypes: allowedInvoiceTypes
			});

			this.messageService.add({
				severity: 'error',
				summary: this.translate.instant('notifications.errorSummary'),
				detail: this.translate.instant('notifications.document.invoiceTypeNotAllowed')
			});
			return false;
		}

		return true;
	}

	/**
	 * Loads companies data for validation purposes
	 * @private
	 */
	private loadCompanies(): void {
		this.service.getAllActiveCompanyByMerchant().subscribe({
			next: (companies: any[]) => {
				this.companies = companies;
				},
			error: (error) => {
				console.error('[DEBUG/DocumentComponent] Error loading companies:', error);
				// Don't block the form if companies fail to load
				this.companies = [];
			}
		});
	}
}